import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Matchup, Team } from '../../types';
import { getCurrentWeek } from '../../utils/typeUtils';
import { getWeekRange, clampWeek } from '../../utils/matchupWeeks';

interface MatchupsCardProps {
  matchups: Matchup[];
  teams: Team[] | null;
  user: { id: number } | null;
  isCommissioner: boolean;
  matchupsLoading: boolean;
  matchupsError: boolean;
  onOpenBoxscore: (id: number) => void;
  onSimulateWeek: (week: number) => void;
  onGenerateSchedule: () => void;
  simulatingWeek: boolean;
  simulateError: string | null;
  scheduleGenerating: boolean;
  scheduleError: string | null;
  scheduleMessage: string | null;
}

export default function MatchupsCard({
  matchups,
  teams,
  user,
  isCommissioner,
  matchupsLoading,
  matchupsError,
  onOpenBoxscore,
  onSimulateWeek,
  onGenerateSchedule,
  simulatingWeek,
  simulateError,
  scheduleGenerating,
  scheduleError,
  scheduleMessage,
}: MatchupsCardProps) {
  const currentWeek = getCurrentWeek(matchups);
  const { minWeek, maxWeek, weeks } = getWeekRange(matchups);

  const [selectedWeek, setSelectedWeek] = useState<number | null>(currentWeek);

  // Re-sync the selected week when the matchup set changes (e.g. schedule
  // generated/refetched). Keep the user's position if it's still in range,
  // otherwise fall back to the current week.
  // Dep is [matchups] only: currentWeek/minWeek/maxWeek are derived from
  // matchups in the render body, so the effect's closure always sees fresh
  // values for the same array identity — stale values are impossible.
  useEffect(() => {
    setSelectedWeek(prev => {
      if (currentWeek === null) return null;
      if (prev === null) return currentWeek;
      return minWeek !== null && maxWeek !== null
        ? clampWeek(prev, minWeek, maxWeek)
        : currentWeek;
    });
  }, [matchups]); // eslint-disable-line react-hooks/exhaustive-deps

  const teamNameMap: Record<number, string> = {};
  (teams || []).forEach(t => { teamNameMap[t.id] = t.team_name || (t as any).teamName || 'Unknown'; });

  const weekMatchups = matchups.filter(m => m.week === selectedWeek);
  const hasIncompleteThisWeek = weekMatchups.some(m => !m.is_completed);

  const myMatchup = weekMatchups.find(
    m => teams?.some(t => t.user_id === user?.id && (t.id === m.team1_id || t.id === m.team2_id))
  );

  const stepPrev = () => {
    if (minWeek === null || maxWeek === null) return;
    setSelectedWeek(w => clampWeek((w ?? minWeek) - 1, minWeek, maxWeek));
  };

  const stepNext = () => {
    if (minWeek === null || maxWeek === null) return;
    setSelectedWeek(w => clampWeek((w ?? maxWeek) + 1, minWeek, maxWeek));
  };

  const isPrevDisabled = minWeek === null || (selectedWeek ?? minWeek) <= minWeek;
  const isNextDisabled = maxWeek === null || (selectedWeek ?? maxWeek) >= maxWeek;
  const showCurrentChip = selectedWeek !== currentWeek && currentWeek !== null;

  return (
    <div className="card">
      <div className="card-header">
        {matchups.length > 0 ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {/* Week stepper — arrows flank the label as one non-wrapping unit */}
            <div className="flex items-center gap-2">
              <button
                aria-label="previous week"
                onClick={stepPrev}
                disabled={isPrevDisabled}
                className="icon-btn w-9 h-9 border border-secondary-300 text-ink-900 transition-colors hover:bg-ink-900 hover:text-paper disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-900"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <span data-testid="week-label" className="font-display uppercase text-base tracking-wide text-ink-900 whitespace-nowrap">
                Week {selectedWeek} <span className="text-secondary-400">/ {maxWeek}</span>
              </span>
              <button
                aria-label="next week"
                onClick={stepNext}
                disabled={isNextDisabled}
                className="icon-btn w-9 h-9 border border-secondary-300 text-ink-900 transition-colors hover:bg-ink-900 hover:text-paper disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-900"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Jump-to-week + current + simulate */}
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={selectedWeek ?? ''}
                onChange={e => {
                  const w = parseInt(e.target.value, 10);
                  if (!Number.isNaN(w)) setSelectedWeek(w);
                }}
                className="text-sm border border-secondary-300 rounded-none px-2 py-2 bg-white"
                aria-label="jump to week"
              >
                {weeks.map(w => {
                  const wMatchups = matchups.filter(m => m.week === w);
                  const allDone = wMatchups.length > 0 && wMatchups.every(m => m.is_completed);
                  const label = allDone
                    ? `Week ${w} ✓ final`
                    : w === currentWeek
                    ? `Week ${w} • this week`
                    : `Week ${w} upcoming`;
                  return <option key={w} value={w}>{label}</option>;
                })}
              </select>

              {showCurrentChip && (
                <button
                  onClick={() => setSelectedWeek(currentWeek)}
                  className="btn-outline-secondary text-xs px-2 py-1"
                >
                  Current
                </button>
              )}

              {isCommissioner && selectedWeek === currentWeek && currentWeek !== null && hasIncompleteThisWeek && (
                <button
                  onClick={() => onSimulateWeek(selectedWeek!)}
                  disabled={simulatingWeek}
                  className="btn-outline-secondary text-sm flex items-center"
                >
                  {simulatingWeek ? (
                    <><div className="loading-spinner w-4 h-4 mr-2" />Simulating...</>
                  ) : (
                    `Simulate Week ${currentWeek}`
                  )}
                </button>
              )}
            </div>
          </div>
        ) : (
          <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">Schedule</h3>
        )}
        {simulateError && (
          <p className="text-xs text-danger-600 mt-2 sm:text-right">{simulateError}</p>
        )}
      </div>

      {matchupsLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="loading-spinner w-6 h-6 mr-3" />
          <span className="text-secondary-600">Loading schedule...</span>
        </div>
      ) : matchupsError ? (
        <div className="card-body">
          <p className="text-danger-600 text-sm">Could not load matchups.</p>
        </div>
      ) : matchups.length === 0 ? (
        /* Empty state — no schedule generated yet */
        <div className="card-body text-center py-10">
          {scheduleMessage ? (
            <p className="text-success-700 font-medium mb-4">{scheduleMessage}</p>
          ) : (
            <>
              <p className="text-secondary-600 mb-2 font-medium">No schedule generated yet.</p>
              {isCommissioner ? (
                <>
                  {scheduleError && (
                    <p className="text-danger-600 text-sm mb-3">{scheduleError}</p>
                  )}
                  <button
                    onClick={onGenerateSchedule}
                    disabled={scheduleGenerating}
                    className="btn-primary flex items-center mx-auto"
                  >
                    {scheduleGenerating ? (
                      <><div className="loading-spinner w-4 h-4 mr-2" />Generating...</>
                    ) : (
                      'Generate Schedule'
                    )}
                  </button>
                  <p className="text-xs text-secondary-400 mt-2">Creates a 17-week round-robin schedule</p>
                </>
              ) : (
                <p className="text-secondary-500 text-sm">Waiting on commissioner to generate the schedule.</p>
              )}
            </>
          )}
        </div>
      ) : (
        /* Matchup rows for selected week */
        <div className="divide-y divide-secondary-100">
          {weekMatchups.map(m => {
            const isMyGame = m.id === myMatchup?.id;
            const t1Name = teamNameMap[m.team1_id] ?? `Team ${m.team1_id}`;
            const t2Name = teamNameMap[m.team2_id] ?? `Team ${m.team2_id}`;
            return (
              <div
                key={m.id}
                data-matchup-row
                onClick={() => onOpenBoxscore(m.id)}
                className={`flex items-center justify-between px-6 py-4 cursor-pointer transition-colors ${isMyGame ? 'bg-primary-50 hover:bg-primary-100' : 'hover:bg-secondary-50'}`}
              >
                {/* Team 1 */}
                <div className="flex-1 min-w-0 text-right">
                  <span className={`block truncate font-medium text-sm ${isMyGame && teams?.find(t => t.user_id === user?.id)?.id === m.team1_id ? 'text-primary-700' : 'text-secondary-900'}`}>
                    {t1Name}
                  </span>
                </div>

                {/* Scores / vs */}
                <div className="mx-3 sm:mx-4 shrink-0 text-center">
                  {m.is_completed ? (
                    <span className="font-display text-lg leading-none tabular-nums whitespace-nowrap">
                      <span className={Number(m.team1_score) >= Number(m.team2_score) ? 'text-ink-900' : 'text-secondary-400'}>
                        {Number(m.team1_score).toFixed(1)}
                      </span>
                      <span className="text-secondary-300 mx-1.5">–</span>
                      <span className={Number(m.team2_score) >= Number(m.team1_score) ? 'text-ink-900' : 'text-secondary-400'}>
                        {Number(m.team2_score).toFixed(1)}
                      </span>
                    </span>
                  ) : (
                    <span className="text-secondary-400 text-xs uppercase tracking-wider">vs</span>
                  )}
                </div>

                {/* Team 2 */}
                <div className="flex-1 min-w-0">
                  <span className={`block truncate font-medium text-sm ${isMyGame && teams?.find(t => t.user_id === user?.id)?.id === m.team2_id ? 'text-primary-700' : 'text-secondary-900'}`}>
                    {t2Name}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
